<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useAerialImageAnnotationModal } from '@/composables/annotation/useAerialImageAnnotationModal'
import MapView from '@/components/map/MapView.vue'
import { useDisasterStore } from '@/stores/disasterStore'
import { useGeoserverStatusStore } from '@/stores/geoserverStatusStore'
import { useAerialAreasStore } from '@/stores/aerialAreasStore'
import { useMapPointCloudStore } from '@/stores/mapPointCloudStore'
import { geoRunGeoserverApi } from '@/api/geoserver/geoRunGeoserverApiGwFuncs'
import { GlobalError } from '@/utils/globalError/globalError'
import { ERROR_CODES } from '@/constants/error/errorCode'
import { useLoadingView } from '@/features/miiaFw/composables/useLoadingView'
import { bulkSetDisasterVisualization } from '@/api/disasterVisualization/bulkSetDisasterVisualizationApiGwFuncs'
import { GEO_SERVER_STATUS } from '@/constants/api/geoserver/geoserverConstants'

const isReady = ref(false)

const { setLoadingView } = useLoadingView()
const aerialImageAnnotationModal = useAerialImageAnnotationModal()
const disasterStore = useDisasterStore()
const geoserverStatusStore = useGeoserverStatusStore()
const aerialAreasStore = useAerialAreasStore()
const mapPointCloudStore = useMapPointCloudStore()

/**
 * マウント時の処理
 * リロードを考慮し災害API、自治体設定API以外の処理はここで定義する
 */
const mountProcessing = async () => {
  // これ以降は自治体コード、災害IDは必ずある想定
  const govCode = disasterStore.govCode
  const disasterId = disasterStore.disasterId
  if (govCode === undefined || disasterId === undefined) return

  // 点群データ登録APIを実行
  await bulkSetDisasterVisualization({ govCode: govCode, disasterId: disasterId })
  // 円グラフデータ取得APIを実行し、Storeに保存
  await mapPointCloudStore.fetchData(govCode, disasterId)
  // 空撮範囲取得
  await aerialAreasStore.fetch({ govCode: govCode, disasterId: disasterId })
  // ジオサーバ起動API実行（災害IDが必要なため、災害API実行後に実施）
  let isEcsServiceCreated = true
  try {
    const { callRunGeoserver } = geoRunGeoserverApi()
    const res = await callRunGeoserver({ govCode: govCode, disasterId: disasterId })
    isEcsServiceCreated = res.result.isServiceCreated
  } catch (err) {
    console.error(err)
    const globalError = new GlobalError([ERROR_CODES.GEO_SERVER_RUN_FAILED])
    globalError.showErrorMessage()
  }
  // ジオサーバ確認用のポーリング処理開始
  // 起動直後はタスク作成までラグがあるため、起動中扱いとして時間をおいてからポーリング処理を開始する
  geoserverStatusStore.startVerifyGeoserverStatus(govCode, disasterId, {
    initializeStatus: GEO_SERVER_STATUS.PROVISIONING,
    // ECSサービス作成直後は時間がかかるため、ディレイを大きめに設定
    startDelayMilliSecond: isEcsServiceCreated ? 40 * 1000 : 15 * 1000
  })

  isReady.value = true
}

onMounted(async () => {
  try {
    setLoadingView(true)
    await mountProcessing()
    // アノテーション選択モーダルを開く
    await aerialImageAnnotationModal.open(false)
  } finally {
    setLoadingView(false)
  }
})
</script>

<template>
  <div v-if="isReady" class="d-flex screening-view">
    <MapView />
  </div>
</template>

<style scoped>
.screening-view {
  height: calc(100% - 60px);
  position: relative;
  z-index: 0;
}
</style>
